import { SelectorComponentProps, SelectorOptionsComponentProps, SelectorTypeProps } from "./Interface";
import { useSelector } from "./hooks/useSelector";
import { DialogOptionsPopUp, DialogPopUp } from "./UI/DialogPopUp";
import { SelectorBase } from "./UI/Utils";

/**
 * This reusable component is responsible for rendering the clickable selector that will open a pop up Dialog box for the user to select a value
 * There will be four different types of selectors:
 * 1. String
 * 2. Number
 * 3. Boolean
 * 4. Date
 * 
 * Each of them will have a different pop up Dialog box that will be rendered based on the type of the selector
 * 
 * >>PARAMS<<
 * @param value - The current value of the selector
 * @param type - The type of the selector
 * @param api_callback - The API callback once the user updates the value
 * @returns The base component for the Table Selector
 */

// Generic Selector that can handle any type
export function Selector({field_name, value, api_callback, type}: SelectorComponentProps & {type: SelectorTypeProps}) {
    const { input_value, open, setOpen, updateValue, setValue } = useSelector(value, api_callback)

    return (
        <>
            <SelectorBase value={input_value} onClick={() => setOpen(true)} />
            <DialogPopUp 
                open={open} 
                setOpen={setOpen} 
                type={type} 
                value={input_value?.toString() ?? ""} 
                setValue={setValue}
                onSave={() => updateValue(field_name, input_value)}
                field_name={field_name} 
            />
        </>
    )
}

// Selector that uses a list of options for the user to select from
// Needs the options to be passed in as this {options: { value: string, label: string }[]}
export function SelectorOptions({field_name, value, api_callback, options, conditionalStyles}: SelectorOptionsComponentProps) {
    const { input_value, open, setOpen, updateValue, setValue } = useSelector(value, api_callback)
    
    return (
        <>
            <SelectorBase value={input_value} onClick={() => setOpen(true)} conditionalStyles={conditionalStyles} />
            <DialogOptionsPopUp 
                open={open} 
                setOpen={setOpen} 
                value={input_value?.toString() ?? ""} 
                setValue={setValue}
                onSave={() => updateValue(field_name, input_value)}
                field_name={field_name} 
                options={options}
            />
        </>
    )
}

// Convenience components for specific types
export function DateSelector({field_name, value, api_callback}: SelectorComponentProps) {
    return <Selector field_name={field_name} value={value} api_callback={api_callback} type="date" />
}

export function StringSelector({field_name, value, api_callback}: SelectorComponentProps) {
    return <Selector field_name={field_name} value={value} api_callback={api_callback} type="string" />
}

export function NumberSelector({field_name, value, api_callback}: SelectorComponentProps) {
    return <Selector field_name={field_name} value={value} api_callback={api_callback} type="number" />
}

export function BooleanSelector({field_name, value, api_callback}: SelectorComponentProps) {
    return <Selector field_name={field_name} value={value} api_callback={api_callback} type="boolean" />
}